import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HERO_SLIDES,
  HERO_SLIDE_INTERVAL_MS,
  HERO_SLIDE_SIZES,
  HERO_SLIDE_TRANSITION_MS,
  type HeroSlideAsset,
} from '../lib/heroSlides'
import './HomeHeroLanding.css'

export type HomeHeroStat = {
  value: string
  label: string
}

export type HomeHeroCta = {
  label: string
  to: string
}

export type HomeHeroLandingProps = {
  brand?: string
  line1: string
  line2: string
  line3: string
  primaryCta: HomeHeroCta
  secondaryCta: HomeHeroCta
  stats: HomeHeroStat[]
  slides?: HeroSlideAsset[]
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function SlidePicture({
  slide,
  alt,
  active,
  priority,
}: {
  slide: HeroSlideAsset
  alt: string
  active: boolean
  priority: boolean
}) {
  return (
    <picture className={`home-hero-slide${active ? ' is-active' : ''}`} aria-hidden={!active}>
      <source
        type="image/webp"
        srcSet={`${slide.webp480} 480w, ${slide.webp768} 768w, ${slide.webp1024} 1024w`}
        sizes={HERO_SLIDE_SIZES}
      />
      <img
        src={slide.png768}
        alt={active ? alt : ''}
        width={slide.width}
        height={slide.height}
        decoding={priority ? 'sync' : 'async'}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'low'}
        draggable={false}
      />
    </picture>
  )
}

export function HomeHeroLanding({
  brand,
  line1,
  line2,
  line3,
  primaryCta,
  secondaryCta,
  stats,
  slides = HERO_SLIDES,
}: HomeHeroLandingProps) {
  const { t } = useTranslation()
  const count = slides.length
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)
  const pointerRef = useRef<{ id: number; x: number; y: number } | null>(null)
  const resumeTimer = useRef<number | null>(null)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    setReduceMotion(prefersReducedMotion())
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduceMotion(mq.matches)
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])

  // Soft desktop parallax (gradient wash only — no large rings)
  useEffect(() => {
    if (reduceMotion) return
    const section = sectionRef.current
    if (!section) return

    let raf = 0
    const update = () => {
      raf = 0
      if (window.matchMedia('(max-width: 1023px)').matches) {
        section.style.setProperty('--hh-parallax', '0px')
        return
      }
      const rect = section.getBoundingClientRect()
      const viewH = window.innerHeight || 1
      const progress = Math.min(1.15, Math.max(-0.15, 1 - rect.top / viewH))
      const y = (progress - 0.5) * 36
      section.style.setProperty('--hh-parallax', `${y.toFixed(1)}px`)
    }

    const onScroll = () => {
      if (raf) return
      raf = window.requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [reduceMotion])

  const goTo = useCallback(
    (next: number) => {
      if (count < 2) return
      setIndex(((next % count) + count) % count)
    },
    [count],
  )

  const pauseTemporarily = useCallback(() => {
    setPaused(true)
    if (resumeTimer.current) window.clearTimeout(resumeTimer.current)
    resumeTimer.current = window.setTimeout(() => setPaused(false), 8000)
  }, [])

  useEffect(() => {
    if (reduceMotion || paused || count < 2) return
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % count)
    }, HERO_SLIDE_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [reduceMotion, paused, count])

  useEffect(() => {
    return () => {
      if (resumeTimer.current) window.clearTimeout(resumeTimer.current)
    }
  }, [])

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    pointerRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerRef.current
    pointerRef.current = null
    if (!start || start.id !== e.pointerId) return
    const dx = e.clientX - start.x
    const dy = e.clientY - start.y
    if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.2) return
    pauseTemporarily()
    const rtl = document.documentElement.getAttribute('dir') === 'rtl'
    const goingNext = rtl ? dx > 0 : dx < 0
    goTo(index + (goingNext ? 1 : -1))
  }

  return (
    <section ref={sectionRef} className="home-hero" aria-labelledby="home-hero-title">
      <div className="home-hero-bg" aria-hidden>
        <span className="home-hero-glow home-hero-glow--a" />
        <span className="home-hero-glow home-hero-glow--b" />
        <span className="home-hero-dot-deco home-hero-dot-deco--1" />
        <span className="home-hero-dot-deco home-hero-dot-deco--2" />
      </div>

      <div className="container home-hero-inner">
        <div className="home-hero-grid">
          <div className="home-hero-content">
            {brand ? <p className="home-hero-brand">{brand}</p> : null}
            <h1 id="home-hero-title" className="home-hero-title">
              <span className="home-hero-line home-hero-line--lead">{line1}</span>
              <span className="home-hero-line home-hero-line--focus">{line2}</span>
              <span className="home-hero-line home-hero-line--tag">{line3}</span>
            </h1>

            <div className="home-hero-cta">
              <Link className="btn btn-primary home-hero-btn home-hero-btn--primary" to={primaryCta.to}>
                {primaryCta.label}
              </Link>
              <Link className="btn home-hero-btn home-hero-btn--secondary" to={secondaryCta.to}>
                {secondaryCta.label}
              </Link>
            </div>
          </div>

          <div className="home-hero-media">
            <div className="home-hero-media-stage">
              {stats.length > 0 && (
                <ul className="home-hero-stats" aria-label="ILIA">
                  {stats.map((stat, i) => (
                    <li
                      key={`${stat.value}-${stat.label}`}
                      className={`home-hero-stat home-hero-stat--${i + 1} home-hero-stat--enter`}
                      style={{ ['--stat-i' as string]: i }}
                    >
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div
                className="home-hero-slider"
                role="region"
                aria-roledescription="carousel"
                aria-label={t('hero.sliderLabel')}
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={() => {
                  pointerRef.current = null
                }}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => {
                  if (!resumeTimer.current) setPaused(false)
                }}
                style={{ ['--hh-transition' as string]: `${HERO_SLIDE_TRANSITION_MS}ms` }}
              >
                <div className="home-hero-slider-frame">
                  {slides.map((slide, i) => (
                    <SlidePicture
                      key={slide.id}
                      slide={slide}
                      alt={slide.alt || (slide.altKey ? t(slide.altKey) : t('hero.mediaAlt'))}
                      active={i === index}
                      priority={i === 0}
                    />
                  ))}
                </div>
              </div>

              {count > 1 ? (
                <div className="home-hero-dots" role="tablist" aria-label={t('hero.sliderLabel')}>
                  {slides.map((slide, i) => (
                    <button
                      key={slide.id}
                      type="button"
                      role="tab"
                      aria-selected={i === index}
                      aria-label={t('hero.slideDot', { n: i + 1 })}
                      className={`home-hero-dot${i === index ? ' is-active' : ''}`}
                      onClick={() => {
                        pauseTemporarily()
                        goTo(i)
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
